/**
 * Odrzucenie zapisu układu przez regułę „jeden blok na layout” (pozycje dokumentu, rejestr VAT).
 *
 * Serwer odpowiada `400 { error: 'LAYOUT_SINGLETON_DUPLICATE', type, types, issues }` na każdym
 * zapisie układu z dwoma blokami tego samego typu (układ tenanta, układy kartoteki/szablonu w
 * panelu ITE). `issues` są po angielsku, więc komunikat dla użytkownika budujemy z PRZETŁUMACZONYCH
 * nazw typów (`types`): każda nazwa we własnych cudzysłowach, liczba mnoga wg liczby typów.
 *
 * Jedno miejsce dla wszystkich edytorów układu, żeby ten sam błąd brzmiał wszędzie tak samo.
 */

import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';

import { LayoutElementRegistry } from '@/components/fields/LayoutElementRegistry';

const LAYOUT_SINGLETON_DUPLICATE = 'LAYOUT_SINGLETON_DUPLICATE';

/**
 * Typy elementów z odpowiedzi `LAYOUT_SINGLETON_DUPLICATE` albo `null`, gdy błąd to inny błąd albo
 * lista `types` jest pusta/nieprawidłowa (wtedy nie ma nazw do pokazania — wołający spada na
 * `issues` albo komunikat ogólny).
 */
export function extractLayoutSingletonTypes(err: unknown): string[] | null {
  if (!isAxiosError(err)) return null;
  const data: unknown = err.response?.data;
  if (typeof data !== 'object' || data === null) return null;
  const { error, types } = data as { error?: unknown; types?: unknown };
  if (error !== LAYOUT_SINGLETON_DUPLICATE) return null;
  if (!Array.isArray(types) || types.length === 0) return null;
  return types.map((type) => String(type));
}

/** Nazwa typu elementu do komunikatów: tłumaczona (`labelKey` z rejestru), inaczej `label`, inaczej `type`. */
export function layoutElementDisplayName(t: TFunction, type: string): string {
  const def = LayoutElementRegistry.get(type);
  const fallback = def?.label ?? type;
  return def?.labelKey ? t(def.labelKey, { defaultValue: fallback }) : fallback;
}

/** Komunikat „Układ nie został zapisany: może zawierać tylko jeden blok …” dla podanych typów. */
export function layoutSingletonRejectedMessage(t: TFunction, types: readonly string[]): string {
  return t('tenant.systemModuleBuilder.toasts.layoutSingletonRejectedOnSave', {
    count: types.length,
    elements: types
      .map((type) =>
        t('tenant.systemModuleBuilder.toasts.quotedName', {
          name: layoutElementDisplayName(t, type),
        }),
      )
      .join(', '),
  });
}
